import {
  edsn_Marktrol,
  MprService,
  RegistrationChangeDto,
} from "../../../openapi/edsn-webapi";
import { Form, OverlayLoader, withFormContext } from "../../common";
import { SubmitHandler, useFormContext, UseFormProps } from "react-hook-form";
import { useMutationWrapper } from "../../../hooks";
import RegistrationChangeFormStepper from "./RegistrationChangeFormStepper";
import { Alert } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import useRegistration from "../useRegistration";
import { useEffect } from "react";

type RegistrationChangeFormProps = {
  id?: string | null;
  token?: string | null;
};

const RegistrationChangeForm = ({ id, token }: RegistrationChangeFormProps) => {
  const isUpdate = id && token;
  const setIsUpdate = useRegistration((state) => state.setIsUpdate);
  const navigate = useNavigate();
  const navigateToSuccess = () => navigate("/registratieformulier/success");
  const permissionDocumentGas = useRegistration(
    (state) => state.permissionDocumentGas
  );
  const permissionDocumentElectricity = useRegistration(
    (state) => state.permissionDocumentElectricity
  );

  const {
    formState: { errors },
  } = useFormContext<RegistrationChangeDto>();
  const { mutate: uploadPermissionDocument, isPending: isLoadingFileUpload } =
    useMutationWrapper(
      ["postApiMprUpload"],
      (formData: { RegistrationId?: string; Files?: Array<Blob> }) =>
        MprService.postApiMprUpload({ formData }),
      { onSuccess: navigateToSuccess }
    );

  const { mutate, isPending: isLoadingSubmit } = useMutationWrapper(
    ["submitMprChange"],
    (requestBody: RegistrationChangeDto) => {
      if (isUpdate) {
        return MprService.putApiMprChange({ id, token, requestBody });
      }
      return MprService.postApiMprChange({ requestBody });
    },
    {
      onSuccess: (data) => {
        if (permissionDocumentGas || permissionDocumentElectricity) {
          uploadPermissionDocument({
            Files: [permissionDocumentGas, permissionDocumentElectricity]
              .filter((m) => !!m)
              .map((n) => n as File),
            RegistrationId: data.registrationId,
          });
        } else {
          navigateToSuccess();
        }
      },
    }
  );

  useEffect(() => {
    setIsUpdate(!!(id && token));
  }, [id, token]);

  const onSubmit: SubmitHandler<RegistrationChangeDto> = (data) => {
    const defaultKnowledgeArea = process.env.REACT_APP_DEFAULT_KNOWLEDGE_AREA;
    const isValidMarketRole =
      data.marketRole === edsn_Marktrol.LVLEVERANCIER ||
      data.marketRole ===
        edsn_Marktrol.GDSEIGENAAR_GESLOTEN_DISTRIBUTIE_SYSTEEM;

    const updatedInvoiceContacts = isValidMarketRole
      ? data.invoiceContacts
          ?.filter((contact) => contact.regardingName || contact.email)
          .map((contact) => ({
            ...contact,
            knowledgeArea: defaultKnowledgeArea,
          })) || []
      : [];

    mutate({ ...data, invoiceContacts: updatedInvoiceContacts });
  };

  return (
    <Form onSubmit={onSubmit}>
      {errors["contactDetailsChanged"] && (
        <Alert severity="error">
          <FormattedMessage id="atleastOne" />
        </Alert>
      )}
      <RegistrationChangeFormStepper />
      {(isLoadingSubmit || isLoadingFileUpload) && <OverlayLoader />}
    </Form>
  );
};

export default withFormContext<
  UseFormProps<RegistrationChangeDto> & RegistrationChangeFormProps,
  RegistrationChangeDto
>(RegistrationChangeForm);
